import {
  utils,
} from '@ama-sdk/core';
import {
  Flight,
} from '@dapi/sdk';
import {
  createFlight,
  createFlightEndPoint,
  createFlightItem,
  MockFlight,
  MockFlightItem,
  todayPlusNdays,
} from '@dapi/sdk/spec';

/**
 * Creates a mock flight object
 *
 * @param departureLocationCode departure location code
 * @param arrivalLocationCode arrival location code
 * @param delayInFlightDateTime delay in flight date time
 * @param operatingAirlineCode operating airline code
 * @param departureTime departure time
 * @param arrivalTime arrival time
 * @param departureTerminal departure terminal number
 * @param arrivalTerminal  arrival terminal number
 * @returns mock flight object
 */
export function createCustomFlight(
  departureLocationCode?: string,
  arrivalLocationCode?: string,
  delayInFlightDateTime?: number,
  operatingAirlineCode?: string,
  departureTime?: string,
  arrivalTime?: string,
  departureTerminal?: string,
  arrivalTerminal?: string
): MockFlight {
  return createFlight({
    template: {
      departure: createFlightEndPoint({
        template: {
          locationCode: departureLocationCode || 'HEL',
          dateTime: new utils.DateTime(todayPlusNdays(departureTime || '09:30', delayInFlightDateTime || 1)),
          terminal: departureTerminal || '2',
          location: {
            type: 'airport',
            cityCode: departureLocationCode || 'HEL'
          }
        }
      }),
      arrival: createFlightEndPoint({
        template: {
          locationCode: arrivalLocationCode || 'TKU',
          dateTime: new utils.DateTime(todayPlusNdays(arrivalTime || '10:55', delayInFlightDateTime || 1)),
          terminal: arrivalTerminal || '3',
          location: {
            type: 'airport',
            cityCode: arrivalLocationCode || 'TKU'
          }
        }
      }),
      isOpenSegment: false,
      secureFlightIndicator: false,
      marketingAirline: 'FINNAIR',
      marketingAirlineCode: operatingAirlineCode,
      operatingAirlineName: 'NORDIC REGIONAL AIRLINES',
      marketingFlightNumber: '635',
      operatingAirlineCode: operatingAirlineCode,
      duration: 2100,
      aircraft: 'AEROSPATIALE/ALENIA ATR 72',
      aircraftCode: 'AT7'
    }
  });
}

export const cabin = 'eco';
export const fareFamilyCode = 'ECOFLEX';

export const FLIGHT1: Flight = {
  departure: createFlightEndPoint({
    template: {
      dateTime: new utils.DateTime(todayPlusNdays('06:10', 15)),
      locationCode: 'NCE',
      terminal: '2'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'CDG',
      dateTime: new utils.DateTime(todayPlusNdays('07:40', 15)),
      terminal: '2F'
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'AF',
  marketingFlightNumber: '7711',
  duration: 5400,
  aircraftCode: '319'
};

export const FLIGHT2: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'CDG',
      dateTime: new utils.DateTime(todayPlusNdays('10:20', 15)),
      terminal: '2E'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'JFK',
      dateTime: new utils.DateTime(todayPlusNdays('12:40', 15)),
      terminal: '4'
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'DL',
  marketingFlightNumber: '3628',
  duration: 30_000,
  aircraftCode: '339'
};

export const FLIGHT3: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'CDG',
      dateTime: new utils.DateTime(todayPlusNdays('11:20', 15)),
      terminal: '2E'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'JFK',
      dateTime: new utils.DateTime(todayPlusNdays('13:50', 15)),
      terminal: '1'
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'AF',
  marketingFlightNumber: '14',
  duration: 30_600,
  aircraftCode: '77W'
};

export const FLIGHT4: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'CDG',
      dateTime: new utils.DateTime(todayPlusNdays('08:40', 15)),
      terminal: '2F'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'AMS',
      dateTime: new utils.DateTime(todayPlusNdays('10:00', 15))
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'KL',
  marketingFlightNumber: '8224',
  duration: 4800,
  aircraftCode: '73W'
};

export const FLIGHT5: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'AMS',
      dateTime: new utils.DateTime(todayPlusNdays('11:00', 15))
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'JFK',
      dateTime: new utils.DateTime(todayPlusNdays('13:20', 15))
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'DL',
  marketingFlightNumber: '5595',
  duration: 30_000,
  aircraftCode: '332'
};

export const FLIGHT6: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'NCE',
      dateTime: new utils.DateTime(todayPlusNdays('06:30', 15)),
      terminal: '2'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'AMS',
      dateTime: new utils.DateTime(todayPlusNdays('08:30', 15))
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'KL',
  marketingFlightNumber: '8270',
  duration: 7200,
  aircraftCode: 'E90'
};

export const FLIGHT7: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'NCE',
      dateTime: new utils.DateTime(todayPlusNdays('09:20', 15)),
      terminal: '2'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'CDG',
      dateTime: new utils.DateTime(todayPlusNdays('10:50', 15)),
      terminal: '2F'
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'AF',
  marketingFlightNumber: '7705',
  duration: 5400,
  aircraftCode: '320'
};

export const FLIGHT8: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'CDG',
      dateTime: new utils.DateTime(todayPlusNdays('13:20', 15)),
      terminal: '2E'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'JFK',
      dateTime: new utils.DateTime(todayPlusNdays('15:45', 15)),
      terminal: '4'
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'DL',
  marketingFlightNumber: '3625',
  duration: 30_300,
  aircraftCode: '333'
};

export const FLIGHT9: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'CDG',
      dateTime: new utils.DateTime(todayPlusNdays('14:20', 15)),
      terminal: '1'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'JFK',
      dateTime: new utils.DateTime(todayPlusNdays('17:00', 15)),
      terminal: '1'
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'AF',
  marketingFlightNumber: '6',
  duration: 31_200,
  aircraftCode: '77W'
};

export const FLIGHT10: Flight = {
  departure: createFlightEndPoint({
    template: {
      locationCode: 'NCE',
      dateTime: new utils.DateTime(todayPlusNdays('09:30', 15)),
      terminal: '2'
    }
  }),
  arrival: createFlightEndPoint({
    template: {
      locationCode: 'ORY',
      dateTime: new utils.DateTime(todayPlusNdays('10:55', 15)),
      terminal: '3'
    }
  }),
  marketingAirline: 'AF',
  marketingAirlineCode: 'AF',
  operatingAirlineCode: 'AF',
  marketingFlightNumber: '6207',
  duration: 5100,
  aircraftCode: '320'
};

/**
 * Flight from CPH to OSL
 */
export const cphOslFlight: Flight = {
  ...createCustomFlight('CPH', 'OSL', 1, '0E'),
  marketingAirline: 'AMADEUS PRESALES 0E',
  marketingAirlineCode: '0E',
  operatingAirline: 'AMADEUS PRESALES 0E',
  operatingAirlineCode: '0E',
  marketingFlightNumber: '635',
  duration: 4200,
  aircraft: 'BOEING 737-700',
  aircraftCode: '73G'
};

/**
 * Flight from CPH to OSL
 */
export const cphOslFlightFixedDate: Flight = {
  arrival: {
    location: {
      airportName: 'COTE D AZUR',
      cityCode: 'OSL',
      type: 'airport'
    },
    locationCode: 'OSL',
    dateTime: new utils.DateTime('2050-04-16T10:00:43Z'),
    terminal: '3'
  },
  departure: {
    location: {
      airportName: 'COTE D AZUR',
      cityCode: 'CPH',
      type: 'airport'
    },
    locationCode: 'CPH',
    dateTime: new utils.DateTime('2050-04-16T10:00:43Z'),
    terminal: '2'
  },
  marketingAirline: 'AMADEUS PRESALES 0E',
  marketingAirlineCode: '0E',
  operatingAirline: 'AMADEUS PRESALES 0E',
  operatingAirlineCode: '0E',
  marketingFlightNumber: '635',
  duration: 4200,
  aircraft: 'BOEING 737-700',
  aircraftCode: '73G'
};

/**
 * Flight from CPH to OSL
 */
export const oslLhrFlight: Flight = {
  ...createCustomFlight('OSL', 'LHR', 3, '0E'),
  marketingAirline: 'AMADEUS PRESALES 0E',
  marketingAirlineCode: '0E',
  operatingAirline: 'AMADEUS PRESALES 0E',
  operatingAirlineCode: '0E',
  marketingFlightNumber: '635',
  duration: 4200,
  aircraft: 'BOEING 737-700',
  aircraftCode: '73G'
};

/**
 * Flight item from CPH to OSL
 */
export const cphOslFlightItem: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST1',
    statusCode: 'TK',
    flight: cphOslFlight,
    cabin,
    fareFamilyCode
  }
});

/**
 * Flight item from OSL to LHR
 */
export const oslLhrFlightItem: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST2',
    statusCode: 'TK',
    flight: oslLhrFlight,
    cabin,
    fareFamilyCode
  }
});

/**
 * Flight from OSL to CPH
 */
export const oslCphFlight: Flight = {
  ...createCustomFlight('OSL', 'CPH', 15, '0E'),
  marketingAirline: 'AMADEUS PRESALES 0E',
  marketingAirlineCode: '0E',
  operatingAirline: 'AMADEUS PRESALES 0E',
  operatingAirlineCode: '0E',
  marketingFlightNumber: '635',
  duration: 4200,
  aircraft: 'BOEING 737-700',
  aircraftCode: '73G'
};

/**
 * Flight from OSL to CPH
 */
export const oslTkuFlight: Flight = {
  ...createCustomFlight('OSL', 'TKU', 15, '0E'),
  marketingAirline: 'AMADEUS PRESALES 0E',
  marketingAirlineCode: '0E',
  operatingAirline: 'AMADEUS PRESALES 0E',
  operatingAirlineCode: '0E',
  marketingFlightNumber: '635',
  duration: 4200,
  aircraft: 'BOEING 737-700',
  aircraftCode: '73G'
};

export const cphLhrFlight: Flight = createCustomFlight('CPH', 'LHR', 5);

export const cphLhrFlightAI: Flight = createCustomFlight('CPH', 'LHR', 5, 'AI');

export const lhrOslFlight: Flight = createCustomFlight('LHR', 'OSL', 7);

export const lhrOslFlightAI: Flight = createCustomFlight('LHR', 'OSL', 7, 'AI', '12:00', '15:00');

/**
 * Flight item from CPH to LHR with schedule change status
 */
export const cphLhrFlightItem: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST1',
    statusCode: 'TK',
    flight: cphLhrFlight,
    cabin,
    fareFamilyCode
  }
});

/**
 * Flight item from LHR to OSL with confirmed status
 */
export const lhrOslFlightItem: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST1',
    statusCode: 'HK',
    flight: lhrOslFlight,
    cabin,
    fareFamilyCode
  }
});

/**
 * Flight item from CPH to OSL with schedule change status
 */
export const cphOslOriginalFlightItem: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST1',
    statusCode: 'TK',
    flight: cphOslFlight,
    cabin,
    fareFamilyCode
  }
});

/**
 * Flight item from OSL to CPH with schedule change status
 */
export const oslCphOriginalFlightItem: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST2',
    statusCode: 'TK',
    flight: oslCphFlight,
    cabin,
    fareFamilyCode
  }
});

/**
 * Flight item from CPH to OSL with schedule change status
 */
export const cphOslOriginalFlightItemWithSameFlight: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST1',
    statusCode: 'TK',
    flight: cphOslFlight,
    cabin,
    fareFamilyCode
  }
});

/**
 * Flight item from CPH to OSL with schedule change status
 */
export const oslCphOriginalFlightItemWithSameFlight: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST2',
    statusCode: 'TK',
    flight: oslCphFlight,
    cabin,
    fareFamilyCode
  }
});

export const cphOslUnOnlyFlightItem: MockFlightItem = {
  ...cphOslFlightItem,
  statusCode: 'UN'
};

export const cphOslUnOnlyFlightItemFixedDate: MockFlightItem = createFlightItem({
  template: {
    bookingClass: 'Y',
    id: 'ST1',
    statusCode: 'UN',
    flight: cphOslFlightFixedDate,
    cabin,
    fareFamilyCode
  }
});

/**
 * Flight item from OSL to CPH
 */
export const oslCphFlightItem: MockFlightItem = createFlightItem({
  template: {
    id: 'ST2',
    flight: oslCphFlight,
    cabin,
    bookingClass: 'Y',
    fareFamilyCode,
    statusCode: 'TK'
  }
});

/**
 * Flight item from OSL to CPH
 */
export const oslTkuFlightItem: MockFlightItem = createFlightItem({
  template: {
    id: 'ST3',
    flight: oslTkuFlight,
    cabin,
    bookingClass: 'Y',
    fareFamilyCode,
    statusCode: 'TK'
  }
});

export const oslCphUnOnlyFlightItem: MockFlightItem = {
  ...oslCphFlightItem,
  statusCode: 'UN'
};
export const oslCphUnOnlyFlightItemDifferentFlightId: MockFlightItem = {
  ...oslCphFlightItem,
  id: 'ST1',
  statusCode: 'UN'
};

/**
 * Flight from HEL to TKU
 */
export const helTkuFlight: Flight = createCustomFlight();

/**
 * Flight item from HEL to TKU
 */
export const helTkuFlightItem: MockFlightItem = createFlightItem({
  template: {
    id: 'ST3',
    flight: helTkuFlight,
    cabin,
    bookingClass: 'Y',
    statusCode: 'TK'
  }
});

/**
 * Flight from TKU to HEL to be used as disrupted flight
 */
export const helTkuOriginalFlightItem: MockFlightItem = createFlightItem({
  template: {
    id: 'ST1',
    flight: helTkuFlight,
    cabin,
    bookingClass: 'Y',
    bookingStatus: {
      name: 'Unable, does not operate'
    },
    statusCode: 'UN'
  }
});

/**
 * Flight from TKU to HEL
 */
export const tkuHelFlight: Flight = createCustomFlight('TKU', 'HEL', 5);

/**
 * Flight item from TKU to HEL
 */
export const tkuHelFlightItem: MockFlightItem = createFlightItem({
  template: {
    id: 'ST4',
    flight: tkuHelFlight,
    cabin,
    bookingClass: 'Y',
    fareFamilyCode,
    statusCode: 'TK'
  }
});

/**
 * Flight item from TKU to HEL to be used as disrupted flight
 */
export const tkuHelOriginalFlightItem: MockFlightItem = createFlightItem({
  template: {
    id: 'ST2',
    flight: tkuHelFlight,
    cabin,
    bookingClass: 'Y',
    bookingStatus: {
      name: 'Unable, does not operate'
    },
    statusCode: 'UN'
  }
});

/**
 * Flight and Flight item from Arn to Ewr
 */
export const arnEwrFlight: MockFlight = createCustomFlight('ARN', 'EWR', 3, 'SK');
export const arnEwrFlightItem: MockFlightItem = createFlightItem({
  template: {
    id: 'ST1',
    cabin,
    bookingClass: 'Y',
    fareFamilyCode,
    statusCode: 'HK',
    flight: arnEwrFlight
  }
});
